import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Plus, CheckCircle2, FileText, Users, LogOut, Loader2, CalendarDays, AlertTriangle, Timer, BarChart3, TrendingUp, ChevronRight, Sun, Moon, Sparkles, Layers, UserPlus, Zap, CalendarCheck, Users2, Tag, X, Eye, ArrowLeft, Search, Share2, Radio, MapPin, Trash2, Mail, Bell, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { US_STATES, DEFAULT_STATE } from '../utils/states';
import InboxPopup from '../components/InboxPopup';
import SharePlanbloxModal from '../components/SharePlanbloxModal';
import DigestToggle from '../components/DigestToggle';
import SmartCityAutocomplete from '../components/SmartCityAutocomplete';
import { getSlotLabel, getSlotStyles, getPrimaryActiveHold } from '../utils/slotLabels';
import CreditBalance from '../components/CreditBalance';
import CreditsCard from '../components/CreditsCard';
import { useLimits } from '../components/LimitIndicator';
import { shouldShowQuota, getQuotaText } from '../utils/quotaDisplay';
import LanguageSelector from '../components/LanguageSelector';
import GuestActionModal from '../components/GuestActionModal';
import HoldsDemo from '../components/HoldsDemo';
import { GUEST_DEMO_DATA } from '../data/guestDemoData';
import OnboardingTour from '../components/OnboardingTour';
import CategorySelector from '../components/CategorySelector';
import GuestSignupBanner from '../components/GuestSignupBanner';
import NotificationsDropdown from '../components/NotificationsDropdown';
import EmailVerificationModal from '../components/EmailVerificationModal';
import InsightsWidget from '../components/hub/InsightsWidget';
import ActivitySummary from '../components/hub/ActivitySummary';
import ActivityFeed from '../components/hub/ActivityFeed';
import MonitoringPlansCard from '../components/hub/MonitoringPlansCard';
import GrowthFeatureCards from '../components/hub/GrowthFeatureCards';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PlanBlox Logo
const PlanBloxLogo = ({ className = "h-8 w-8" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_b5e1c113-3c1b-4c22-aa5b-d15e9e791e88/artifacts/tf4zn6ok_App%20Icon%201%20.png"
    alt="PlanBlox"
    className={className}
  />
);



const PlannerHub = () => {
  const { t } = useTranslation();
  const { user, getAuthHeaders, logout, refreshCreditBalance, creditBalance, hasConfirmedBalance, isGuestMode, exitGuestMode } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { limits, refreshLimits } = useLimits();
  const [events, setEvents] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeHolds, setActiveHolds] = useState([]);
  const [expiringHolds, setExpiringHolds] = useState([]);
  const [privateEventsCount, setPrivateEventsCount] = useState(0);
  const [privateEventsList, setPrivateEventsList] = useState([]);
  const [groupsList, setGroupsList] = useState([]);
  const [plannerCirclesList, setPlannerCirclesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', location: '', city: '', state: DEFAULT_STATE, category: '', tags: [] });
  const [tagInput, setTagInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedHoldEvent, setSelectedHoldEvent] = useState(null);
  // Guest action modal state
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [showHoldsDemo, setShowHoldsDemo] = useState(false);
  // Onboarding tour state - show for first-time authenticated users
  const [showTour, setShowTour] = useState(false);
  // Email verification banner & modal state
  const [verifyBannerDismissed, setVerifyBannerDismissed] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  // Deep Search CTA — always visible for authenticated users
  const [deepSearchDismissed] = useState(false);
  // Monitoring alerts for Hub popup
  const [monitoringAlerts, setMonitoringAlerts] = useState([]);

  useEffect(() => {
    if (!isGuestMode && user && !localStorage.getItem('planblox_onboarding_done')) {
      const timer = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, [isGuestMode, user]);

  const handleTourComplete = () => {
    localStorage.setItem('planblox_onboarding_done', 'true');
    setShowTour(false);
  };

  // Helper function to gate actions for guests
  const handleGuestAction = (callback) => {
    if (isGuestMode) {
      setGuestModalOpen(true);
      return;
    }
    callback();
  };

  const fetchData = useCallback(async () => {
    // In guest mode, use static demo data instead of API calls
    if (isGuestMode) {
      setEvents(GUEST_DEMO_DATA.events);
      setConversations(GUEST_DEMO_DATA.conversations);
      setActiveHolds(GUEST_DEMO_DATA.activeHolds);
      setExpiringHolds(GUEST_DEMO_DATA.expiringHolds);
      setPrivateEventsCount(GUEST_DEMO_DATA.privateEvents.length);
      setPrivateEventsList(GUEST_DEMO_DATA.privateEvents);
      setGroupsList(GUEST_DEMO_DATA.groups ? [GUEST_DEMO_DATA.groups].flat() : []);
      setPlannerCirclesList(GUEST_DEMO_DATA.plannerCircles ? [GUEST_DEMO_DATA.plannerCircles].flat() : []);
      setLoading(false);
      return;
    }
    
    try {
      const [eventsRes, convsRes, holdsRes, expiringRes, privateRes, groupsRes, circlesRes] = await Promise.all([
        axios.get(`${API}/events`, { headers: getAuthHeaders() }),
        axios.get(`${API}/conversations`, { headers: getAuthHeaders() }),
        axios.get(`${API}/events/active-holds`, { headers: getAuthHeaders() }),
        axios.get(`${API}/holds/expiring?hours=24`, { headers: getAuthHeaders() }),
        axios.get(`${API}/private-events`, { headers: getAuthHeaders() }),
        axios.get(`${API}/groups`, { headers: getAuthHeaders() }),
        axios.get(`${API}/planner-circles`, { headers: getAuthHeaders() }),
      ]);
      setEvents(eventsRes.data);
      setConversations(convsRes.data);
      setActiveHolds(holdsRes.data);
      setExpiringHolds(expiringRes.data);
      setPrivateEventsCount(privateRes.data.length);
      setPrivateEventsList(privateRes.data);
      setGroupsList(groupsRes.data);
      setPlannerCirclesList(circlesRes.data);
      // Fetch monitoring alerts separately (non-blocking)
      try {
        const alertsRes = await axios.get(`${API}/discover/monitoring/alerts`, { headers: getAuthHeaders() });
        setMonitoringAlerts(alertsRes.data || []);
      } catch { /* silently fail — monitoring alerts are optional */ }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, isGuestMode]);

  useEffect(() => {
    fetchData();
    // Refresh data every minute to update expiration times (skip for guests)
    if (!isGuestMode) {
      const interval = setInterval(fetchData, 60000);
      return () => clearInterval(interval);
    }
  }, [fetchData, isGuestMode]);

  const handleCreateEvent = async () => {
    if (!newEvent.title.trim()) {
      toast.error('Please enter an event title');
      return;
    }
    if (!newEvent.state) {
      toast.error('Please select a state');
      return;
    }
    if (!newEvent.city.trim()) {
      toast.error('Please select a city');
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/events`, newEvent, { headers: getAuthHeaders() });
      toast.success('Event created!');
      // Reset form state first
      setNewEvent({ title: '', location: '', city: '', state: DEFAULT_STATE, category: '', tags: [] });
      setTagInput('');
      // Close dialog with a slight delay to ensure cleanup
      setCreateDialogOpen(false);
      // Small timeout to ensure dialog fully unmounts before navigation
      setTimeout(() => {
        // Reset any body scroll locks
        document.body.style.overflow = '';
        document.body.style.position = '';
        navigate(`/events/${response.data.id}`);
      }, 100);
    } catch (error) {
      toast.error('Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = () => {
    if (isGuestMode) {
      // Navigate first, then exit guest mode to avoid race condition with route protection
      navigate('/');
      // Small delay to ensure navigation happens before state change
      setTimeout(() => exitGuestMode(), 100);
    } else {
      logout();
      navigate('/login');
    }
  };

  const draftEvents = events.filter(e => e.status === 'Draft');
  const publishedEvents = events.filter(e => e.status === 'Published');

  const getActiveHoldsCount = (event) => {
    let count = 0;
    if (event.hold1?.status === 'Active') count++;
    if (event.hold2?.status === 'Active') count++;
    if (event.hold3?.status === 'Active') count++;
    return count;
  };

  const formatLastMessage = (timestamp) => {
    if (!timestamp) return 'No messages';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 w-full max-w-full overflow-x-hidden">
      {/* Guest Action Modal */}
      <GuestActionModal open={guestModalOpen} onOpenChange={setGuestModalOpen} />
      
      {/* Holds Demo for Guests */}
      {showHoldsDemo && <HoldsDemo onClose={() => setShowHoldsDemo(false)} />}
      
      {/* Onboarding Tour */}
      {showTour && <OnboardingTour onComplete={handleTourComplete} />}
      
      {/* Guest Signup Banner */}
      <GuestSignupBanner />
      
      {/* Header */}
      <header className="border-b bg-card w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-shrink-0">
            <PlanBloxLogo className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl" />
            <LanguageSelector />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-btn">
              {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </Button>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
            <Button 
              size="sm" 
              onClick={() => handleGuestAction(() => navigate('/my-stats'))} 
              data-testid="mystats-nav-btn" 
              className="bg-[#7FD1C6] hover:bg-[#6BC1B6] text-gray-800 border-0 text-xs sm:text-sm px-2 sm:px-3"
            >
              <BarChart3 className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Stats</span>
            </Button>
            {/* Bell - Navigate to Notifications page */}
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => handleGuestAction(() => navigate('/directory'))}
              data-testid="notifications-bell"
            >
              <Bell className="h-5 w-5" />
            </Button>
            {/* Clickable Profile - navigates to account */}
            <button 
              onClick={() => handleGuestAction(() => navigate('/account'))}
              className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm hover:bg-accent px-2 sm:px-3 py-2 rounded-lg transition-colors flex-shrink-0"
              data-testid="profile-btn"
            >
              {isGuestMode ? (
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Eye className="h-4 w-4 text-blue-600" />
                </div>
              ) : (
                <img 
                  src={user?.profilePhotoUrl} 
                  alt={user?.fullName}
                  className="w-8 h-8 rounded-full"
                />
              )}
              <span className="font-medium hidden sm:inline">{isGuestMode ? t('guest.guestBadge', 'Guest') : user?.fullName}</span>
            </button>
            {!isGuestMode && (
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="text-xs">
                <LogOut className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Log Out</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Expiring Holds Warning Banner */}
      {expiringHolds.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="h-4 w-4 text-amber-700" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  {expiringHolds.length} hold{expiringHolds.length > 1 ? 's' : ''} expiring within 24 hours
                </p>
                <p className="text-xs text-amber-600">
                  {expiringHolds.slice(0, 2).map(h => `${h.eventTitle} (${h.hoursRemaining}h)`).join(', ')}
                  {expiringHolds.length > 2 && ` and ${expiringHolds.length - 2} more`}
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="border-amber-300 text-amber-800 hover:bg-amber-100"
                onClick={() => navigate(`/events/${expiringHolds[0].eventId}`)}
              >
                <Timer className="h-4 w-4 mr-1" />
                Review
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Guest Mode: Back to Welcome + Exit */}
        {isGuestMode && (
          <div className="flex items-center gap-3 mb-4" data-testid="guest-nav-bar">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { 
                navigate('/'); 
                setTimeout(() => exitGuestMode(), 100);
              }}
              className="text-sm"
              data-testid="guest-back-welcome-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('hub.backToWelcome')}
            </Button>
          </div>
        )}
        {/* Monitoring Alert Popups — Dismissible */}
        {monitoringAlerts.length > 0 && (
          <div className="space-y-2 mb-4" data-testid="hub-monitoring-alerts">
            {monitoringAlerts.slice(0, 3).map(alert => {
              const styles = {
                red: 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30',
                orange: 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-950/30',
                activity: 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30',
              };
              const iconColor = {
                red: 'text-red-600',
                orange: 'text-orange-600',
                activity: 'text-blue-600',
              };
              return (
                <div
                  key={alert.id}
                  className={`p-3 rounded-lg border ${styles[alert.alert_type] || styles.activity} flex items-start gap-3`}
                  data-testid={`hub-alert-${alert.id}`}
                >
                  {alert.alert_type === 'red' ? (
                    <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconColor.red}`} />
                  ) : alert.alert_type === 'orange' ? (
                    <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconColor.orange}`} />
                  ) : (
                    <Sparkles className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconColor.activity}`} />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold">{alert.title}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{alert.description}</p>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/discover/monitoring/alerts/${alert.id}/dismiss`, {}, { headers: getAuthHeaders() });
                        setMonitoringAlerts(prev => prev.filter(a => a.id !== alert.id));
                      } catch {}
                    }}
                    className="text-muted-foreground hover:text-foreground flex-shrink-0"
                    data-testid={`dismiss-hub-alert-${alert.id}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {/* Credit Low Nudge */}
        {!isGuestMode && creditBalance !== null && creditBalance <= 2 && (
          <div className="mb-4 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 flex items-center justify-between" data-testid="credit-low-nudge">
            <div className="flex items-center gap-2">
              <Coins className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm text-amber-700 dark:text-amber-300">
                You have <strong>{creditBalance}</strong> credit{creditBalance !== 1 ? 's' : ''} remaining
              </span>
            </div>
            <Button variant="outline" size="sm" className="text-xs border-amber-300 hover:bg-amber-100 dark:border-amber-700 dark:hover:bg-amber-900/30" onClick={() => navigate('/credits')} data-testid="credit-low-buy-btn">
              Buy More
            </Button>
          </div>
        )}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">PlanBlox Planner Hub</h1>
            <p className="text-muted-foreground mt-1">
              {isGuestMode 
                ? t('guest.hubDescription', 'Guest mode lets you preview PlanBlox. Sign up to create events, groups, circles, and save your plans.')
                : 'Manage your events and coordinate holds'
              }
            </p>
          </div>
          {!isGuestMode && (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 text-xs"
              onClick={() => setShareOpen(true)}
              data-testid="hub-invite-planners-btn"
            >
              <Share2 className="h-3.5 w-3.5 mr-1.5" />
              Share PlanBlox — Earn Credits
            </Button>
          )}
        </div>

        {/* Deep Search Intelligence CTA */}
        {!isGuestMode && !deepSearchDismissed && (
          <div 
            className="mb-6 p-4 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 cursor-pointer hover:shadow-md transition-all"
            onClick={() => navigate('/deep-search')}
            data-testid="deep-search-cta-card"
          >
            <div className="flex items-start gap-4">
              <div className="p-2.5 bg-emerald-100 rounded-lg shrink-0">
                <Sparkles className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-sm text-emerald-900">Event Intelligence for Planners</h3>
                <p className="text-xs text-emerald-700 mt-1 leading-relaxed">
                  Refine how PlanBlox AI finds and analyzes competition or overlapping events. 
                  Not just a one-time search — includes monitoring your event window and getting alerts.
                </p>
              </div>
              <Button 
                size="sm" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shrink-0 text-xs"
                onClick={(e) => { e.stopPropagation(); navigate('/deep-search'); }}
                data-testid="deep-search-cta-btn"
              >
                <Search className="h-3 w-3 mr-1" /> Deep Search
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Events */}
          <div className="lg:col-span-2 space-y-6">
            {/* Activity Summary - Today & This Week */}
            <div className="space-y-3">
              {/* Ambient Credits Indicator - subtle, clickable */}
              <div className="flex items-center justify-end">
                <button
                  onClick={() => handleGuestAction(() => navigate('/credits'))}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="ambient-credits-indicator"
                >
                  {isGuestMode ? 'Credits · 0' : `Credits · ${hasConfirmedBalance ? creditBalance : '—'}`}
                </button>
              </div>
              
              <ActivitySummary isGuestMode={isGuestMode} getAuthHeaders={getAuthHeaders} onGuestAction={() => setGuestModalOpen(true)} />
            </div>

            {/* My Event Holds */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-amber-600" />
                  Event Holds
                  <Badge variant="secondary" className="ml-1">{draftEvents.length} Holds</Badge>
                </h2>
                {draftEvents.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => isGuestMode ? setShowHoldsDemo(true) : navigate('/calendar?filter=holds')}
                    data-testid="view-all-holds-btn"
                  >
                    {t('hub.viewAll')}
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>
              {draftEvents.length === 0 ? (
                <Card 
                  className="border-dashed cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                  onClick={() => handleGuestAction(() => navigate('/create'))}
                  data-testid="create-first-hold-card"
                >
                  <CardContent className="pt-4 text-center py-6">
                    <p className="text-muted-foreground font-medium text-sm">Go to Create & Edit to add your first event hold</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  <Select onValueChange={(eventId) => setSelectedHoldEvent(eventId)}>
                    <SelectTrigger className="w-full" data-testid="event-holds-dropdown">
                      <SelectValue placeholder="Select an event to view details..." />
                    </SelectTrigger>
                    <SelectContent>
                      {draftEvents.slice(0, 30).map(event => (
                        <SelectItem 
                          key={event.id} 
                          value={event.id}
                          data-testid={`event-option-${event.id}`}
                        >
                          {event.title}{event.city || event.state ? `, ${event.city || ''}${event.city && event.state ? ', ' : ''}${event.state || ''}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Selected Event Card with List Creation */}
                  {selectedHoldEvent && (() => {
                    const event = draftEvents.find(e => e.id === selectedHoldEvent);
                    if (!event) return null;
                    return (
                      <Card className="bg-white dark:bg-card border-2 border-primary/20">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div 
                              className="cursor-pointer flex-1"
                              onClick={() => isGuestMode ? setShowHoldsDemo(true) : navigate(`/events/${event.id}`)}
                            >
                              <h3 className="font-semibold">{event.title}</h3>
                              {(event.city || event.state) && (
                                <p className="text-sm text-muted-foreground">
                                  {event.city}{event.city && event.state ? ', ' : ''}{event.state}
                                </p>
                              )}
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => isGuestMode ? setShowHoldsDemo(true) : navigate(`/events/${event.id}`)}
                              data-testid={`view-holds-${event.id}`}
                            >
                              View Holds
                              <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Published Events */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    {t('hub.publishedEvents')}
                    <Badge variant="secondary" className="ml-1">{publishedEvents.length} Events</Badge>
                  </h2>
                  <p className="text-sm text-muted-foreground ml-7">{t('hub.publishedPublicCalendar')}</p>
                </div>
                {publishedEvents.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => isGuestMode ? setGuestModalOpen(true) : navigate('/calendar?filter=published')}
                    data-testid="view-all-published-btn"
                  >
                    {t('hub.viewAll')}
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>
              {publishedEvents.length === 0 ? (
                <Card 
                  className="border-dashed cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/30 dark:hover:bg-emerald-950/10 transition-colors"
                  onClick={() => handleGuestAction(() => navigate('/create'))}
                  data-testid="create-first-published-card"
                >
                  <CardContent className="pt-4 text-center py-6">
                    <p className="text-muted-foreground font-medium text-sm">Go to Create & Edit to add your first published event</p>
                  </CardContent>
                </Card>
              ) : (
                <Select onValueChange={(eventId) => isGuestMode ? setGuestModalOpen(true) : navigate(`/events/${eventId}`)}>
                  <SelectTrigger className="w-full" data-testid="published-events-dropdown">
                    <SelectValue placeholder="Select an event to view details..." />
                  </SelectTrigger>
                  <SelectContent>
                    {publishedEvents.slice(0, 30).map(event => (
                      <SelectItem 
                        key={event.id} 
                        value={event.id}
                        data-testid={`published-event-option-${event.id}`}
                      >
                        {event.title}{event.city || event.state ? `, ${event.city || ''}${event.city && event.state ? ', ' : ''}${event.state || ''}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Private Events List */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Zap className="h-5 w-5 text-violet-600" />
                    {t('hub.privateEvents')}
                  <Badge variant="secondary" className="ml-1">{privateEventsList.length} Events</Badge>
                  {/* Contextual quota indicator - only when near/at limit */}
                  {limits?.private_events && shouldShowQuota(limits.private_events.current, limits.private_events.free_limit) && (
                    <span className="text-xs font-normal text-muted-foreground ml-2">
                      {getQuotaText(limits.private_events.current, limits.private_events.free_limit)}
                    </span>
                  )}
                </h2>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => isGuestMode ? setGuestModalOpen(true) : navigate('/private-events')}
                  data-testid="view-all-private-events-btn"
                >
                  View All
                  <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
              {privateEventsList.length === 0 ? (
                <Card 
                  className="border-dashed bg-white dark:bg-card cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 dark:hover:bg-violet-950/10 transition-colors"
                  onClick={() => isGuestMode ? setGuestModalOpen(true) : navigate('/create')}
                  data-testid="create-first-private-event-card"
                >
                  <CardContent className="pt-4 text-center py-6">
                    <p className="text-muted-foreground font-medium text-sm">Go to Create & Edit to add your first private event</p>
                  </CardContent>
                </Card>
              ) : (
                <Select onValueChange={(eventId) => navigate(`/get-it-done?eventId=${eventId}`)}>
                  <SelectTrigger className="w-full" data-testid="private-events-dropdown">
                    <SelectValue placeholder="Select an event to view details..." />
                  </SelectTrigger>
                  <SelectContent>
                    {privateEventsList.slice(0, 30).map(event => (
                      <SelectItem 
                        key={event.id} 
                        value={event.id}
                        data-testid={`private-event-option-${event.id}`}
                      >
                        {event.name}{event.city || event.state ? `, ${event.city || ''}${event.city && event.state ? ', ' : ''}${event.state || ''}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* My Planner Circles */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Users2 className="h-5 w-5 text-cyan-600" />
                  {t('hub.eventPlannerCircles', 'Planner Circles')}
                  <Badge variant="secondary" className="ml-1">{plannerCirclesList.length} {plannerCirclesList.length === 1 ? 'Circle' : 'Circles'}</Badge>
                </h2>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => isGuestMode ? setGuestModalOpen(true) : navigate('/planner-circles')}
                  data-testid="view-all-circles-btn"
                >
                  View All
                  <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
              {plannerCirclesList.length === 0 ? (
                <Card 
                  className="border-dashed cursor-pointer hover:border-cyan-300 hover:bg-cyan-50/30 dark:hover:bg-cyan-950/10 transition-colors"
                  onClick={() => isGuestMode ? setGuestModalOpen(true) : navigate('/create')}
                  data-testid="create-first-circle-card"
                >
                  <CardContent className="pt-4 text-center py-6">
                    <p className="text-muted-foreground font-medium text-sm">Go to Create & Edit to add your first planner circle</p>
                  </CardContent>
                </Card>
              ) : (
                <Select onValueChange={(circleId) => isGuestMode ? setGuestModalOpen(true) : navigate(`/planner-circles/${circleId}`)}>
                  <SelectTrigger className="w-full" data-testid="circles-dropdown">
                    <SelectValue placeholder="Select a circle to view details..." />
                  </SelectTrigger>
                  <SelectContent>
                    {plannerCirclesList.slice(0, 30).map(circle => (
                      <SelectItem 
                        key={circle.id} 
                        value={circle.id}
                        data-testid={`circle-option-${circle.id}`}
                      >
                        {circle.name} ({circle.members?.length || 1} {(circle.members?.length || 1) === 1 ? 'member' : 'members'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* My Invite Groups */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Layers className="h-5 w-5 text-teal-600" />
                  {t('hub.inviteGroups', 'Invite Groups')}
                  <Badge variant="secondary" className="ml-1">{groupsList.length} {groupsList.length === 1 ? 'Group' : 'Groups'}</Badge>
                </h2>
                {groupsList.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => isGuestMode ? setGuestModalOpen(true) : navigate('/groups')}
                    data-testid="view-all-groups-btn"
                  >
                    {t('hub.viewAll')}
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>
              {groupsList.length === 0 ? (
                <Card 
                  className="border-dashed cursor-pointer hover:border-teal-300 hover:bg-teal-50/30 dark:hover:bg-teal-950/10 transition-colors"
                  onClick={() => isGuestMode ? setGuestModalOpen(true) : navigate('/create')}
                  data-testid="create-first-group-card"
                >
                  <CardContent className="pt-4 text-center py-6">
                    <p className="text-muted-foreground font-medium text-sm">Go to Create & Edit to add your first invite group</p>
                  </CardContent>
                </Card>
              ) : (
                <Select onValueChange={(groupId) => isGuestMode ? setGuestModalOpen(true) : navigate(`/groups/${groupId}`)}>
                  <SelectTrigger className="w-full" data-testid="groups-dropdown">
                    <SelectValue placeholder="Select a group to view details..." />
                  </SelectTrigger>
                  <SelectContent>
                    {groupsList.slice(0, 30).map(group => (
                      <SelectItem 
                        key={group.id} 
                        value={group.id}
                        data-testid={`group-option-${group.id}`}
                      >
                        {group.name} ({group.emailCount || 0} {(group.emailCount || 0) === 1 ? 'member' : 'members'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Right Column - Growth & Invite */}
          <div className="space-y-6">
            {/* Monitoring Plans */}
            <MonitoringPlansCard getAuthHeaders={getAuthHeaders} isGuestMode={isGuestMode} onGuestAction={() => setGuestModalOpen(true)} />
            {!isGuestMode && <DigestToggle getAuthHeaders={getAuthHeaders} />}
            {/* Activity Feed */}
            <ActivityFeed getAuthHeaders={getAuthHeaders} isGuestMode={isGuestMode} />
            {/* Growth Feature Cards */}
            <GrowthFeatureCards />
          </div>
        </div>

        {/* Market Insights Widget - Full Width at Bottom */}
        <div className="mt-6">
          <InsightsWidget getAuthHeaders={getAuthHeaders} navigate={navigate} isGuestMode={isGuestMode} personalStats={{ holds: draftEvents.length, published: publishedEvents.length, private: privateEventsList.length }} />
        </div>

      </main>

      {/* Inbox Notification Popup - Only for authenticated users, not guests */}
      {!isGuestMode && <InboxPopup />}
    </div>
  );
};

// Activity Feed Component

export default PlannerHub;
